import {ITranscription, ITranscriptionSegment} from '../../interfaces/ITranscriptionManager';
export class SpeakerNameManager {
    private transcription: ITranscription;

    constructor(transcription: ITranscription) {
        this.transcription = transcription;
    }

    public UpdateSpeakerName(segmentIndex: number, speakerName: string, updateAll: boolean) {
        if (this.transcription.speakerNames == null || this.transcription.speakerNames.length == 0) {
            // When updating a transcript that has a null or empty speaker names array, make sure there's at least one speaker ID for all the transcript segments.
            this.transcription.speakerNames = [null];
            this.transcription.transcriptSegments.forEach((segment) => segment.speakerId = 0);
        }

        const oldSpeakerId = this.transcription.transcriptSegments[segmentIndex].speakerId;

        if (updateAll) {
            this.UpdateAllSegments(this.transcription.transcriptSegments[segmentIndex], speakerName);
        } else {
            this.UpdateSingleSegment(segmentIndex, speakerName);
        }

        // If we orphaned (i.e. no segment is pointing to it) a speakerId, delete it.
        if (oldSpeakerId != null) {
            this.DeleteSpeakerIdIfOrphaned(oldSpeakerId);
        }
    }

    // Returns a speaker ID with the given name or null if it doesn't exist.
    private FindSpeakerIdWithName(speakerName: string): number | null {
        if (this.transcription.speakerNames == null)
            return null;

        const speakerId = this.transcription.speakerNames.indexOf(speakerName);

        if (speakerId == -1)
            return null;

        return speakerId;
    }

    // Updates all segments that currently reference currentSpeakerId to targetSpeakerId.
    private UpdateSegmentsWithSpeakerId(currentSpeakerId: number | null, targetSpeakerId: number | null) {
        this.transcription.transcriptSegments.forEach((segment: ITranscriptionSegment) => {
            if (segment.speakerId == currentSpeakerId)
                segment.speakerId = targetSpeakerId;
        });
    }

    // Creates a new speakerId with the given name. Returns the speakerId.
    private CreateNewSpeakerIdWithName(speakerName: string): number {
        this.transcription.speakerNames!.push(speakerName);
        return this.transcription.speakerNames!.length - 1;
    }

    // Updates only this segment name to the given name.
    private UpdateSingleSegment(segmentIndex: number, speakerName: string) {
        const transcriptSegments = this.transcription.transcriptSegments;
        const segment = this.transcription.transcriptSegments[segmentIndex];

        // Try to find an existing speakerId with the name we want.
        let targetSpeakerId = this.FindSpeakerIdWithName(speakerName);

        // If we didn't find one...
        if (targetSpeakerId == null) {
            const doOtherSegmentsPointToThisSpeakerId = () => transcriptSegments.filter((s, index) => s.speakerId == segment.speakerId && index != segmentIndex).length > 0;

            // If this segment's speakerId is null or there are other segments using this speakerId we can't just update its name (because we would update the others too), so create a new one.
            if (segment.speakerId == null || doOtherSegmentsPointToThisSpeakerId()) {
                targetSpeakerId = this.CreateNewSpeakerIdWithName(speakerName);
            } else { // Otherwise we can use this speakerId. Just update its name.
                this.transcription.speakerNames![segment.speakerId] = speakerName;
                return;
            }
        }

        // Set the segment speakerId to targetSpeakerId (which is either a new speakerId or the one we found with the desired name).
        segment.speakerId = targetSpeakerId;
    }

    // Updates all segments with this segment's speakerId to the given name.
    private UpdateAllSegments(segment: ITranscriptionSegment, speakerName: string) {
        // Try to find an existing speakerId with the name we want.
        let targetSpeakerId = this.FindSpeakerIdWithName(speakerName);

        if (targetSpeakerId == null) {
            // If we didn't find one and this segment has a speakerId, just update this segment speakerId's name
            if (segment.speakerId != null) {
                this.transcription.speakerNames![segment.speakerId] = speakerName;
                return;
            } else { // If this segment doesn't have a speakerId, create a new one.
                targetSpeakerId = this.CreateNewSpeakerIdWithName(speakerName);
            }
        }

        // Set all segments that currently point to targetSpeakerId (which is either a new speakerId or the one we found with the desired name).
        this.UpdateSegmentsWithSpeakerId(segment.speakerId!, targetSpeakerId);
    }

    // Deletes the given speakerId if no segment is pointing to it.
    private DeleteSpeakerIdIfOrphaned(speakerId: number) {
        // If we find any segment pointing to our speakerId we can't delete it, so just return.
        for (let i = 0; i < this.transcription.transcriptSegments.length; i++) {
            if (this.transcription.transcriptSegments[i].speakerId == speakerId)
                return;
        }

        // No-one is using this speaker ID, so let's delete it from the speakerNames array.
        this.transcription.speakerNames!.splice(speakerId, 1);

        // Speaker IDs to the right of the deleted one have shifted, so adjust all segments referencing them.
        this.transcription.transcriptSegments.forEach((segment: ITranscriptionSegment) => {
            if (segment.speakerId! > speakerId)
                segment.speakerId!--;
        });
    }
}